/**
 * @file api-notification-log.ts
 * @description API-клиент журнала уведомлений и списка инвайтов (phase19).
 *
 * Endpoints:
 *  - GET /admin/shop/notification-log        (SHOP_MANAGE_SETTINGS)
 *  - GET /admin/shop/customer-invitations    (SHOP_MANAGE_CUSTOMERS)
 */

import { apiFetch } from "./api-client";
import type { NotificationChannel, NotificationKind } from "./api-notification-templates";

// ─── Журнал уведомлений ─────────────────────────────────────────────────────

export type NotificationLogStatus = "SENT" | "MOCK" | "QUEUED_RETRY" | "SKIPPED";

export interface NotificationLogItem {
  id: string;
  createdAt: string;
  kind: NotificationKind;
  channel: NotificationChannel;
  status: NotificationLogStatus;
  skipReason: string | null;
  /** Получатель в маскированном виде (PII в журнале не хранится). */
  recipient: string | null;
  subject: string | null;
  messageId: string | null;
  customer: { id: string; displayName: string | null } | null;
  order: { id: string; number: string } | null;
}

export function listNotificationLog(query?: {
  kind?: NotificationKind;
  channel?: NotificationChannel;
  status?: NotificationLogStatus;
  page?: number;
  limit?: number;
}): Promise<{ items: NotificationLogItem[]; total: number }> {
  const params = new URLSearchParams();
  if (query?.kind) params.set("kind", query.kind);
  if (query?.channel) params.set("channel", query.channel);
  if (query?.status) params.set("status", query.status);
  if (query?.page) params.set("page", String(query.page));
  if (query?.limit) params.set("limit", String(query.limit));
  const qs = params.toString();
  return apiFetch(`/admin/shop/notification-log${qs ? `?${qs}` : ""}`);
}

// ─── Инвайты в ЛК ───────────────────────────────────────────────────────────

export type InvitationStatus = "pending" | "activated" | "expired" | "superseded";

export interface InvitationItem {
  id: string;
  source: "MANAGER_INVITE" | "GUEST_PURCHASE" | "SELF_SIGNUP";
  createdAt: string;
  expiresAt: string;
  consumedAt: string | null;
  supersededAt: string | null;
  status: InvitationStatus;
  customer: { id: string; displayName: string | null; accountActivated: boolean };
  createdBy: { id: string; displayName: string | null } | null;
}

export interface InvitationsResult {
  items: InvitationItem[];
  total: number;
  /** Сводка по магазину: всего / активировано / ожидает. */
  summary: { total: number; activated: number; pending: number };
}

export function listCustomerInvitations(query?: {
  status?: InvitationStatus;
  page?: number;
  limit?: number;
}): Promise<InvitationsResult> {
  const params = new URLSearchParams();
  if (query?.status) params.set("status", query.status);
  if (query?.page) params.set("page", String(query.page));
  if (query?.limit) params.set("limit", String(query.limit));
  const qs = params.toString();
  return apiFetch(`/admin/shop/customer-invitations${qs ? `?${qs}` : ""}`);
}

export const INVITATION_STATUS_LABELS: Record<InvitationStatus, string> = {
  pending: "Ожидает",
  activated: "Активирован",
  expired: "Истёк",
  superseded: "Замещён новым",
};

export const INVITATION_SOURCE_LABELS: Record<InvitationItem["source"], string> = {
  MANAGER_INVITE: "От менеджера",
  GUEST_PURCHASE: "После гостевой покупки",
  SELF_SIGNUP: "Саморегистрация",
};

export const LOG_STATUS_LABELS: Record<NotificationLogStatus, string> = {
  SENT: "Отправлено",
  MOCK: "Mock (dev)",
  QUEUED_RETRY: "В очереди на повтор",
  SKIPPED: "Пропущено",
};

export const LOG_SKIP_REASON_LABELS: Record<string, string> = {
  no_recipient: "нет контакта",
  no_consent: "нет согласия",
  template_missing: "нет шаблона",
  channel_unavailable: "канал недоступен",
  channel_unconfigured: "канал не настроен",
};
